import { ChatRoomContext } from '@/contexts/ChatRoomContext';
import { CommonConfigContext } from '@/contexts/ConfigContext';
import { FullScreenContext } from '@/contexts/FullscreenContext';
import { GlobalDataContext } from '@/contexts/GlobalDataContext';
import { LiteUIContext } from '@/contexts/LiteUIContext';
import { ScreenContext } from '@/contexts/ScreenContext';
import { WebSocketContext } from '@/contexts/WebSocketContext';
import { POPUP_TYPE } from '@/models/Popup';
import { ROUTE_GAME } from '@/models/Route';
import { GameType as GameTypeEnum } from '@/models/games/enums/GameType';
import { getHostById } from '@/modules/host/slice';
import {
    getLandingState,
    getMainGiftState,
    getMainUserState,
    getPromotionToolState,
} from '@/modules/main/selector';
import { promotionToolSliceActions } from '@/modules/main/slice/promotiontool/promotionTool';
import { PromotionPage } from '@/modules/popup/promotiontool/components/PromotionDialog';
import { popupSliceActions } from '@/modules/popup/slice';
import { routeSliceActions } from '@/modules/routeController/slice';
import { RootState } from '@/store/store';
import { gotoOldUI, gotoOldWeb } from '@/utils/commonFunc';
import {
    GA_EVENT_CATEGORY,
    GA_EVENT_LABEL,
    GA_LOBBY_EVENT_NAME,
    GA_SETTING_EVENT_NAME,
    getNavTab,
    sendGA,
} from '@/utils/ga';
import {
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { usePromotionTool } from './popup/promotiontool/usePromotionTool';
import { useGameFeature } from './useGameFeature';
import { BetViewMode } from './useGlobalData';
import { useIsLobby } from './useIsLobby';

const reference = {
    list: {
        icoSizePortrait: 36 * 1.25,
        icoSizeLandscape: 44 * 1.5,
        stepIndexPortrait: 2,
        stepIndexLandscape: 3,
    },
    switch: {
        icoSizePortrait: 32 * 1.25,
        icoSizeLandscape: 48 * 1.5,
        tooltipSizePortrait: 23,
        tooltipSizeLandscape: 38,
        // tooltipSizePortrait: 28,
        // tooltipSizeLandscape: 48,
        stepIndexPortrait: 2,
        stepIndexLandscape: 3,
    },
    tooltip: {
        stepIndexPortrait: 0,
        stepIndexLandscape: 2,
    },
    betview: {
        icoSizePortrait: 86,
        icoSizeLandscape: 116,
        checkBoxSizePortrait: 26.3,
        checkBoxSizeLandscape: 31.9,
    },
    logout: {
        icoSizePortrait: 38,
        icoSizeLandscape: 60,
        stepIndexPortrait: 0,
        stepIndexLandscape: 2,
    },
};

enum ItemList {
    ExploreRewards,
    BetRecord,
    GiftRecord,
    MemberReport,
    GameRules,
    Language,
    Sound,
    Agreement,
    Feedback,
    ChangePassword,
}
const data = {
    list: {
        [ItemList.ExploreRewards]: 'promotion_tool.explore_rewards',
        [ItemList.BetRecord]: 'menu.bet_record',
        [ItemList.GiftRecord]: 'menu.gift_record',
        // [ItemList.MemberReport]:'menu.member_report',
        [ItemList.GameRules]: 'menu.game_rules',
        [ItemList.Language]: 'menu.language',
        [ItemList.Sound]: 'menu.sound',
        // [ItemList.Agreement]:'menu.agreement',
        [ItemList.Feedback]: 'menu.feedback',
        // [ItemList.ChangePassword]:'menu.change_password'
    },
    logout: 'menu.logout',
};

export type useSettingMenuState = ReturnType<typeof useSettingMenu>;
export const useSettingMenu = () => {
    const location = useLocation();
    const [hostId, setHostId] = useState(NaN);
    const dispatch = useDispatch();
    const { isLandscape, scaleFactor } = useContext(ScreenContext);

    const { GameType, ChatEnabled } = useSelector((state: RootState) =>
        getHostById(state, hostId)
    );
    const { canBack, canBackWeb } = useSelector(getLandingState);
    const { disableLogoutLobby, hideFeedback } =
        useContext(CommonConfigContext);
    // const { LobbyType:lobby } = useSelector((state: RootState) => getMainUserState(state));
    const { IsTrial } = useSelector(getMainUserState);
    const isShowEvent = useSelector(
        (state: RootState) => getPromotionToolState(state).Id > 0
    );
    const isOpenPromotionDialog = useSelector((state: RootState) =>
        getPromotionToolState(state)
    ).IsOpenDialog;
    const { eventEnded } = usePromotionTool();

    const isLobby = useIsLobby();
    const { changeClientUI } = useContext(WebSocketContext);

    const { isLite, setIsLite } = useContext(LiteUIContext);
    const [switchLite, setSwitchLite] = useState(isLite);
    const { betView, setBetView } = useContext(GlobalDataContext);

    const { hideFullScreen } = useContext(CommonConfigContext);
    const { isFullScreen, toggleFullScreen } = useContext(FullScreenContext);
    const { lobbyGiftEnable, currencyGiftEnable } =
        useSelector(getMainGiftState);
    const {
        isChatPanelEnabled,
        setIsChatPanelEnabled,
        showOtherPlayerGift,
        setShowOtherPlayerGift,
    } = useContext(ChatRoomContext);
    const { isChatRoomOn } = useGameFeature();

    const [drawerOpen, setDrawerOpen] = useState(false);

    const [tooltipLiteModeOpen, setTooltipLiteModeOpen] = useState(false);
    const [tooltipBetAreaPositionOpen, setTooltipBetAreaPositionOpen] =
        useState(false);

    const [isGiftRecordOpen, setIsGiftRecordOpen] = useState(false);
    const [isBetRecordOpen, setIsBetRecordOpen] = useState(false);
    const [isMemberReportOpen, setIsMemberReportOpen] = useState(false);
    const [isGameRulesOpen, setIsGameRulesOpen] = useState(false);
    const [isLanguageOpen, setIsLanguageOpen] = useState(false);
    const [isSoundOpen, setIsSoundOpen] = useState(false);
    const [isAgreementOpen, setIsAgreementOpen] = useState(false);
    const [isFeedBackOpen, setIsFeedBackOpen] = useState(false);
    const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
    const [showBack, setShowBack] = useState(false);
    const [showBackWeb, setShowBackWeb] = useState(false);
    const [switchZoom, setSwitchZoom] = useState(1);
    const [switchBetView, setSwitchBetView] = useState(betView);

    const closeDrawer = (currentSwitchBetView?: BetViewMode) => {
        const _switchBetView = currentSwitchBetView ?? switchBetView;
        if (betView != _switchBetView) {
            let gaKey: GA_SETTING_EVENT_NAME | undefined = undefined;
            if (_switchBetView === BetViewMode.Original)
                gaKey = GA_SETTING_EVENT_NAME.BET_AREA_DOWN;
            else if (_switchBetView === BetViewMode.Reverse)
                gaKey = GA_SETTING_EVENT_NAME.BET_AREA_UP;
            if (gaKey !== undefined)
                sendGA(
                    gaKey,
                    GA_EVENT_CATEGORY.ENGAGEMENT,
                    GA_EVENT_LABEL.SETTING
                );
            setBetView(_switchBetView);
        }
        if (isLite != switchLite) {
            setIsLite(switchLite);
            const gaKey: GA_SETTING_EVENT_NAME = switchLite
                ? GA_SETTING_EVENT_NAME.LITE_ON
                : GA_SETTING_EVENT_NAME.LITE_OFF;
            sendGA(gaKey, GA_EVENT_CATEGORY.ENGAGEMENT, GA_EVENT_LABEL.SETTING);
            // dispatch(routeSliceActions.goto(ROUTE_LOBBY));
            dispatch(routeSliceActions.refreshTo(location.pathname));
        }

        setTooltipLiteModeOpen(false);
        setTooltipBetAreaPositionOpen(false);
        setDrawerOpen(false);
    };

    const logout = () => {
        dispatch(
            popupSliceActions.open(
                'system.logout_confirm',
                POPUP_TYPE.LOGOUT_CONFIRM
            )
        );
    };

    const onClickSetting = () => {
        setDrawerOpen(true);
        sendGA(
            GA_LOBBY_EVENT_NAME.MENU,
            GA_EVENT_CATEGORY.ENGAGEMENT,
            getNavTab()
        );
    };

    const onClickMenuItem = (key: string) => {
        switch (key) {
            case 'promotion_tool.explore_rewards':
                sendGA(
                    GA_SETTING_EVENT_NAME.EXPLORE_REWARDS,
                    GA_EVENT_CATEGORY.ENGAGEMENT,
                    getNavTab()
                );

                if (eventEnded()) {
                    dispatch(promotionToolSliceActions.isOpenDialog(false));
                    dispatch(
                        popupSliceActions.open(
                            'promotion_tool.popup_event_ended'
                        )
                    );
                    return;
                }

                batch(() => {
                    dispatch(
                        promotionToolSliceActions.updatePage(
                            PromotionPage.Gacha
                        )
                    );
                    dispatch(promotionToolSliceActions.isOpenDialog(true));
                });
                break;
            case 'menu.bet_record':
                setIsBetRecordOpen(true);
                break;
            case 'menu.gift_record':
                setIsGiftRecordOpen(true);
                break;
            case 'menu.member_report':
                setIsMemberReportOpen(true);
                break;
            case 'menu.game_rules':
                setIsGameRulesOpen(true);
                break;
            case 'menu.language':
                setIsLanguageOpen(true);
                break;
            case 'menu.sound':
                setIsSoundOpen(true);
                break;
            case 'menu.agreement':
                setIsAgreementOpen(true);
                break;
            case 'menu.feedback':
                setIsFeedBackOpen(true);
                break;
            case 'menu.change_password':
                setIsChangePasswordOpen(true);
                break;
        }
    };

    const onClickBackToOldClient = () => {
        sendGA(
            GA_SETTING_EVENT_NAME.BACK_TO_PRIOR,
            GA_EVENT_CATEGORY.ENGAGEMENT,
            GA_EVENT_LABEL.SETTING
        );
        changeClientUI();
        if (showBack) {
            gotoOldUI();
        } else if (showBackWeb) {
            gotoOldWeb();
        }
    };

    const enableBetView = useMemo(() => {
        const url = window.location.href;
        if (url.includes(ROUTE_GAME)) {
            switch (GameType) {
                case GameTypeEnum.Blackjack:
                case GameTypeEnum.Roulette:
                case GameTypeEnum.SicBo:
                case GameTypeEnum.ThaiHiLo:
                    // case GameTypeEnum.FishPrawnCrab:
                    return false;
                default:
                    return true;
            }
        } else {
            return false;
        }
    }, [GameType]);

    const originBetViewStyle = useMemo(() => {
        if (!isLandscape && enableBetView) {
            if (switchBetView === BetViewMode.Original) {
                return 'bet-view-button-selected';
            } else {
                return 'bet-view-button';
            }
        } else {
            return 'bet-view-button';
        }
    }, [enableBetView, isLandscape, switchBetView]);

    const reverseBetViewStyle = useMemo(() => {
        if (!isLandscape && enableBetView) {
            if (switchBetView === BetViewMode.Reverse) {
                return 'bet-view-button-selected';
            } else {
                return 'bet-view-button';
            }
        } else {
            return 'bet-view-button';
        }
    }, [switchBetView, enableBetView, isLandscape]);

    //initialization
    useEffect(() => {
        if (hideFeedback) {
            data.list[ItemList.Feedback] = '';
        }

        // if(lobby != LobbyType.Credit){
        //     data.list[ItemList.ChangePassword] = '';
        // }

        if (disableLogoutLobby) data.logout = '';
    }, []);

    //Update data list item by server data
    useEffect(() => {
        data.list[ItemList.ExploreRewards] = isShowEvent
            ? 'promotion_tool.explore_rewards'
            : '';
    }, [isShowEvent]);

    useEffect(() => {
        data.list[ItemList.GiftRecord] =
            lobbyGiftEnable && currencyGiftEnable && !IsTrial
                ? 'menu.gift_record'
                : '';
    }, [lobbyGiftEnable, currencyGiftEnable]);

    useEffect(() => {
        setSwitchZoom(scaleFactor * (isLandscape ? 3 : 2));
    }, [scaleFactor]);

    useEffect(() => {
        const url = window.location.href;
        if (url.includes(ROUTE_GAME)) {
            switch (GameType) {
                case GameTypeEnum.AndarBahar:
                case GameTypeEnum.Baccarat:
                case GameTypeEnum.Blackjack:
                case GameTypeEnum.Dragon:
                case GameTypeEnum.PokDeng:
                case GameTypeEnum.Roulette:
                case GameTypeEnum.SeDie:
                case GameTypeEnum.SicBo:
                case GameTypeEnum.TeenPatti2020:
                    setShowBack(canBack);
                    setShowBackWeb(canBackWeb);
                    break;
                default:
                    setShowBack(false);
                    setShowBackWeb(false);
                    break;
            }
        } else {
            setShowBack(canBack);
            setShowBackWeb(canBackWeb);
        }
    }, [GameType, canBack, canBackWeb, isLobby]);

    useEffect(() => {
        if (isOpenPromotionDialog) {
            closeDrawer();
        }
    }, [isOpenPromotionDialog]);

    useLayoutEffect(() => {
        closeDrawer();
    }, [switchLite]);

    return {
        showOtherPlayerGift,
        setShowOtherPlayerGift,
        lobbyGiftEnable,
        isChatPanelEnabled,
        setIsChatPanelEnabled,
        isChatRoomOn,
        ChatEnabled,
        setHostId,
        reference,
        data,
        isLite,
        setSwitchLite,
        switchLite,
        hideFullScreen,
        isFullScreen,
        toggleFullScreen,
        drawerOpen,
        setDrawerOpen,
        closeDrawer,
        tooltipLiteModeOpen,
        setTooltipLiteModeOpen,
        tooltipBetAreaPositionOpen,
        setTooltipBetAreaPositionOpen,
        isGiftRecordOpen,
        setIsGiftRecordOpen,
        isBetRecordOpen,
        setIsBetRecordOpen,
        isMemberReportOpen,
        setIsMemberReportOpen,
        isGameRulesOpen,
        setIsGameRulesOpen,
        isLanguageOpen,
        setIsLanguageOpen,
        isSoundOpen,
        setIsSoundOpen,
        isAgreementOpen,
        setIsAgreementOpen,
        isFeedBackOpen,
        setIsFeedBackOpen,
        isChangePasswordOpen,
        setIsChangePasswordOpen,
        showBack,
        showBackWeb,
        switchZoom,
        setSwitchBetView,
        enableBetView,
        originBetViewStyle,
        reverseBetViewStyle,
        onClickSetting,
        onClickMenuItem,
        onClickBackToOldClient,
        logout,
    };
};
